import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Divider,
  Grid,
  LinearProgress,
  Tabs,
  Tab,
  Tooltip,
  Alert,
  AlertTitle,
  Button,
} from "@mui/material";

import Auxiliarydata_speedometer from "./Auxiliarydata_speedometer";
import { buildAuxData } from "src/app/utils/helper";
import useWebSocket from "react-use-websocket";
import DashboardApi from "../dashboard/dashboardApi";
import { makeStyles } from "tss-react/mui";
import AuxiliaryTrendDialog from "./AuxiliaryTrendDialog";

const useStyles = makeStyles()((theme) => {
  return {
    trendsTabRoot: {
      minHeight: "auto",
    },
    tabsRoot: {
      height: "34px",
      minHeight: "34px",
    },
    tabRoot: {
      background: "#fff",
      borderRadius: "4px",
      border: "1px solid #f7f7f7",
      height: "34px",
      minHeight: "34px",
      "&.Mui-selected": {
        background: theme.palette.primary.main,
        color: "#fff",
      },
    },
    tabIndicator: {
      display: "none",
    },
    activeTab: {
      background: theme.palette.primary.main,
      color: "#fff",
    },
  };
});

const FileBrowserPage = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [globalIndicator, setGlobalIndicator] = useState<any>([]);
  const [isWebsocketConnect, setIsWebSocketConnect] = useState(true);
  const [moduleTabs, setModuleTabs] = useState<any[]>([]);
  const [activeModule, setActiveModule] = useState<number>(0);
  const [isTrendLoading, setIsTrendLoading] = useState<boolean>(false);
  const [isTrendDialogOpen, setIsTrendDialogOpen] = useState<boolean>(false);
  const [trendErrorMessage, setTrendErrorMessage] = useState<string>("");
  const [trendSeriesOptions, setTrendSeriesOptions] = useState<any[]>([]);
  const [selectedTrendSeriesKeys, setSelectedTrendSeriesKeys] =
    useState<string[]>([]);
  const [selectedTrendIndicator, setSelectedTrendIndicator] =
    useState<any>(null);
  const [trendExportRows, setTrendExportRows] = useState<any[]>([]);
  const [trendExportColumns, setTrendExportColumns] = useState<string[]>([]);
  const [trendExportModuleName, setTrendExportModuleName] =
    useState<string>("module");
  const [trendDateRange, setTrendDateRange] = useState<any>(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    return {
      startDate: formatDate(startDate),
      endDate: formatDate(endDate),
    };
  });
  const { classes } = useStyles();

  const toPayloadDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const parseDate = (input: string) => {
    if (!input) {
      return null;
    }

    const normalized = input.replace(/\//g, "-").replace(" ", "T");
    const parsedDate = new Date(normalized);
    if (!Number.isNaN(parsedDate.getTime())) {
      return parsedDate;
    }

    const fallbackDate = new Date(input);
    if (!Number.isNaN(fallbackDate.getTime())) {
      return fallbackDate;
    }

    return null;
  };

  const extractRowJson = (row: any) => {
    if (!row || row.jsondata === undefined || row.jsondata === null) {
      return null;
    }

    if (typeof row.jsondata === "object") {
      return row.jsondata;
    }

    if (typeof row.jsondata === "string") {
      try {
        return JSON.parse(row.jsondata);
      } catch (error) {
        return null;
      }
    }

    return null;
  };

  const getModuleFileName = (moduleData: any) => {
    try {
      const fromData = JSON.parse(moduleData?.from_data || "{}");
      const assetName = fromData?.asset_name || "asset";
      const equipmentName = fromData?.equipment_name || "equipment";
      return `${assetName}_${equipmentName}`
        .replace(/\s+/g, "_")
        .replace(/[^a-zA-Z0-9_-]/g, "");
    } catch (error) {
      return "module";
    }
  };

  const loadTrendData = async (indicator: any, useAutoExpandRange = true) => {
    const activeModuleData = moduleTabs[activeModule];
    if (!activeModuleData?.id) {
      setTrendErrorMessage("Please select a module to load historical data.");
      return;
    }

    setTrendSeriesOptions([]);
    setSelectedTrendSeriesKeys([]);
    setTrendExportRows([]);
    setTrendExportColumns([]);
    setTrendErrorMessage("");
    setIsTrendLoading(true);

    try {
      let rows: any[] = [];
      const startDateValue = trendDateRange?.startDate;
      const endDateValue = trendDateRange?.endDate;

      if (!startDateValue || !endDateValue) {
        setTrendErrorMessage("Please select both start and end dates.");
        setIsTrendLoading(false);
        return;
      }

      if (useAutoExpandRange) {
        const lookbackDays = [7, 30, 180, 3650];
        const endDate = new Date(`${endDateValue}T23:59:59`);

        for (const days of lookbackDays) {
          const startDate = new Date(endDate);
          startDate.setDate(startDate.getDate() - days);

          try {
            const fetchedRows = await DashboardApi.getAuxiliaryTrendRows({
              start_date: toPayloadDate(startDate),
              end_date: toPayloadDate(endDate),
              rpm_min: 0,
              rpm_max: 0,
              module_id: activeModuleData.id,
            });

            if (Array.isArray(fetchedRows) && fetchedRows.length > 0) {
              rows = fetchedRows;
              break;
            }
          } catch (rangeError: any) {
            const message = (
              rangeError?.Message ||
              rangeError?.message ||
              ""
            ).toString();

            if (!message.toLowerCase().includes("no record found")) {
              throw rangeError;
            }
          }
        }
      } else {
        rows = await DashboardApi.getAuxiliaryTrendRows({
          start_date: `${startDateValue} 00:00:00`,
          end_date: `${endDateValue} 23:59:59`,
          rpm_min: 0,
          rpm_max: 0,
          module_id: activeModuleData.id,
        });
      }

      if (!rows.length) {
        setTrendErrorMessage("No historical records found for this module.");
        setIsTrendLoading(false);
        return;
      }

      const metadataKeys = [
        "ID",
        "DateAndTime",
        "DeviceType",
        "DeviceName",
        "DeviceAddress",
      ];
      const seriesByKey: Record<string, any> = {};
      const exportRowsByTimestamp: Record<string, any> = {};
      const exportColumnSet = new Set<string>();

      setTrendExportModuleName(getModuleFileName(activeModuleData));

      for (const row of rows || []) {
        const parsedRow = extractRowJson(row);
        if (!parsedRow || typeof parsedRow !== "object") {
          continue;
        }

        for (const topLevelKey of Object.keys(parsedRow)) {
          if (!topLevelKey.startsWith("Aux")) {
            continue;
          }

          const auxNode = parsedRow[topLevelKey];
          if (!auxNode || typeof auxNode !== "object") {
            continue;
          }

          const rowDateString = auxNode?.DateAndTime || parsedRow?.DateAndTime;
          const parsedDate = parseDate(rowDateString);
          if (!parsedDate) {
            continue;
          }

          for (const paramKey of Object.keys(auxNode)) {
            if (metadataKeys.includes(paramKey)) {
              continue;
            }

            const parameterNode = auxNode[paramKey];
            const rawUnit = String(parameterNode?.Unit || "").trim();
            const isWattUnit = /^(w|watt|watts)$/i.test(rawUnit);
            const resolvedUnit = isWattUnit ? "kW" : rawUnit;

            const rawValue = Number(parameterNode?.Value);
            const numericValue = isWattUnit ? rawValue / 1000 : rawValue;
            if (!Number.isFinite(numericValue)) {
              continue;
            }

            const seriesKey = `${topLevelKey}__${paramKey}`;
            const exportColumn = `${topLevelKey}_${paramKey}`;
            exportColumnSet.add(exportColumn);

            if (!seriesByKey[seriesKey]) {
              seriesByKey[seriesKey] = {
                key: seriesKey,
                title: `${topLevelKey} ${parameterNode?.Desc || paramKey} (${resolvedUnit || ""})`,
                unit: resolvedUnit || "",
                points: [],
              };
            }

            seriesByKey[seriesKey].points.push({
              timestamp: parsedDate.getTime(),
              label: parsedDate.toLocaleString(),
              value: numericValue,
            });

            const rowTimestamp = String(parsedDate.getTime());
            if (!exportRowsByTimestamp[rowTimestamp]) {
              exportRowsByTimestamp[rowTimestamp] = {
                DateTime: parsedDate.toLocaleString(),
                _timestamp: parsedDate.getTime(),
              };
            }
            exportRowsByTimestamp[rowTimestamp][exportColumn] = numericValue;
          }
        }
      }

      const normalizedSeries = Object.values(seriesByKey)
        .map((series: any) => {
          series.points.sort((a: any, b: any) => a.timestamp - b.timestamp);
          return {
            ...series,
            labels: series.points.map((point: any) => point.label),
            values: series.points.map((point: any) => point.value),
          };
        })
        .filter((series: any) => series.values.length > 0);

      if (!normalizedSeries.length) {
        setTrendErrorMessage(
          "No auxiliary parameters found in historical records for this module.",
        );
        setIsTrendLoading(false);
        return;
      }

      setTrendSeriesOptions(normalizedSeries);

      const sortedColumns = Array.from(exportColumnSet).sort((a, b) =>
        a.localeCompare(b),
      );
      const normalizedExportRows = Object.values(exportRowsByTimestamp)
        .sort((a: any, b: any) => a._timestamp - b._timestamp)
        .map((row: any) => {
          const cleaned: any = {
            DateTime: row.DateTime,
          };
          for (const column of sortedColumns) {
            cleaned[column] = row[column] ?? "";
          }
          return cleaned;
        });

      setTrendExportColumns(["DateTime", ...sortedColumns]);
      setTrendExportRows(normalizedExportRows);

      const requestedSeriesKey =
        indicator?.auxKey && indicator?.parameterKey
          ? `${indicator.auxKey}__${indicator.parameterKey}`
          : "";
      const hasRequestedSeries = normalizedSeries.some(
        (series: any) => series.key === requestedSeriesKey,
      );
      const defaultSelectedKey = hasRequestedSeries
        ? requestedSeriesKey
        : normalizedSeries[0].key;
      setSelectedTrendSeriesKeys(defaultSelectedKey ? [defaultSelectedKey] : []);
    } catch (error: any) {
      setTrendErrorMessage(
        error?.Message ||
          error?.message ||
          "Unable to load trend data for this parameter.",
      );
    } finally {
      setIsTrendLoading(false);
    }
  };

  const handleShowTrend = (indicator: any) => {
    setSelectedTrendIndicator(indicator || null);
    setIsTrendDialogOpen(true);
    loadTrendData(indicator, true);
  };

  const onTrendDateRangeChange = (
    field: "startDate" | "endDate",
    value: string,
  ) => {
    setTrendDateRange((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  const onApplyTrendDateRange = () => {
    loadTrendData(selectedTrendIndicator, false);
  };

  const onDownloadTrendExcel = () => {
    if (!trendExportRows.length || !trendExportColumns.length) {
      setTrendErrorMessage("No data available to download.");
      return;
    }

    const escapeCsv = (value: any) => {
      const text = value === null || value === undefined ? "" : String(value);
      return `"${text.replace(/"/g, '""')}"`;
    };

    const header = trendExportColumns.map(escapeCsv).join(",");
    const body = trendExportRows
      .map((row: any) =>
        trendExportColumns.map((column) => escapeCsv(row[column])).join(","),
      )
      .join("\n");
    const csv = `${header}\n${body}`;

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${trendExportModuleName}_aux_trend_${trendDateRange.startDate}_to_${trendDateRange.endDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const { sendMessage, lastMessage } = useWebSocket(
    process.env.REACT_APP_WEBSOCKET_URL ||
      `ws:${window.location.hostname}:8081`,
    {
      onMessage: () => {
        const processName = moduleTabs?.[activeModule]?.process_name;
        if (sendMessage && processName) {
          sendMessage(processName);
        }
      },
      onError: (e) => {
        setIsLoading(false);
      },
      shouldReconnect: (closeEvent) => true,
    },
    isWebsocketConnect,
  );

  useEffect(() => {
    return () => setIsWebSocketConnect(false);
  }, []);

  useEffect(() => {
    if (moduleTabs.length > 0) {
      if (moduleTabs[activeModule].process_name) {
        setIsLoading(true);
        sendMessage(moduleTabs[activeModule].process_name);
      } else {
        setIsLoading(false);
      }
    }
  }, [moduleTabs, activeModule]);

  useEffect(() => {
    if (lastMessage !== undefined) {
      setIsLoading(true);
      const data = lastMessage?.data;
      if (data) {
        try {
          const parsedData = buildAuxData(data);
          setIsLoading(false);
          setGlobalIndicator(parsedData);
        } catch (ex) {
          console.error(ex);
          setIsLoading(false);
        }
      }
    } else {
      setIsLoading(false);
    }
  }, [lastMessage]);

  useEffect(() => {
    setIsLoading(true);
    DashboardApi.getModules()
      .then((res) => {
        if (res.length) {
          setModuleTabs(res || []);
        } else {
          setIsLoading(false);
        }
      })
      .catch((e) => {
        setModuleTabs([]);
        setIsLoading(false);
      });
  }, []);
  const onActiveModuleChange = (_event: any, params: any) => {
    setActiveModule(params);
  };

  return (
    <Box>
      {isLoading && (
        <Box sx={{ my: 1 }}>
          <LinearProgress />
        </Box>
      )}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography
          variant="h5"
          padding={2}
          sx={{ fontWeight: 600, fontSize: "24px" }}
        >
          Auxiliary Data
        </Typography>
        <Button
          variant="outlined"
          sx={{ ml: 1 }}
          onClick={() => handleShowTrend(null)}
        >
          Show Trend
        </Button>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <Box sx={{ ml: 1 }}>
            <Tabs
              value={activeModule}
              onChange={onActiveModuleChange}
              aria-label="select modules"
              classes={{
                root: classes.tabsRoot,
                indicator: classes.tabIndicator,
              }}
              variant="scrollable"
              scrollButtons="auto"
            >
              {moduleTabs?.map((tabElement: any, index: number) => {
                return (
                  <Tooltip
                    title={tabElement.name}
                    key={`tabElement${tabElement?.id}`}
                  >
                    <Tab
                      label={`${
                        JSON.parse(tabElement.from_data).asset_name
                      } - ${JSON.parse(tabElement.from_data).equipment_name}`}
                      // {...tabProps(index)}
                      classes={{
                        root: classes.tabRoot,
                        selected: classes.activeTab,
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Tabs>
          </Box>
        </Box>
      </Box>
      <Box sx={{ bgcolor: "white", mt: "15px" }}>
        <Divider sx={{ mb: "10px" }} />
        <Grid container spacing={3}>
          {globalIndicator && globalIndicator.length > 0 ? (
            globalIndicator.map((val: any) => (
              <Auxiliarydata_speedometer val={val} key={`aux${val?.id}`} />
            ))
          ) : (
            <Box sx={{ width: "100%" }}>
              <Alert sx={{ display: "flex", width: "100%" }} severity="error">
                <AlertTitle>
                  No auxiliary data is available for this module or auxiliary
                  device is not integrated with the system
                </AlertTitle>
              </Alert>
            </Box>
          )}
        </Grid>
      </Box>
      <AuxiliaryTrendDialog
        open={isTrendDialogOpen}
        onClose={() => setIsTrendDialogOpen(false)}
        title="Auxiliary Trend"
        seriesOptions={trendSeriesOptions}
        selectedSeriesKeys={selectedTrendSeriesKeys}
        onSeriesChange={setSelectedTrendSeriesKeys}
        dateRange={trendDateRange}
        onDateRangeChange={onTrendDateRangeChange}
        onApplyDateRange={onApplyTrendDateRange}
        onDownloadExcel={onDownloadTrendExcel}
        canDownload={trendExportRows.length > 0}
        isLoading={isTrendLoading}
        errorMessage={trendErrorMessage}
      />
    </Box>
  );
};
export default FileBrowserPage;
